import { useEffect, useRef, useState } from 'react';

import type { Message } from '../types';

interface ResumedChat {
  chatId: number;
  messages: Message[];
}

interface Options {
  open: boolean;
  loadChat: () => Promise<ResumedChat | null>;
  onEmpty?: () => void;
  onResume: (resumed: ResumedChat) => void;
  hasStarted: () => boolean;
}

export function useChatResume({ open, loadChat, onEmpty, onResume, hasStarted }: Options): boolean {
  const [resuming, setResuming] = useState(false);
  // Guards the fetch to a single attempt per mount; the effect otherwise re-runs
  // whenever the callback props change identity (every render).
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (!open || hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;
    setResuming(true);

    void (async () => {
      try {
        const resumed = await loadChat();
        if (hasStarted()) return;

        if (resumed) onResume(resumed);
        else onEmpty?.();
      } catch {
        // Resume is best-effort; on failure the sidebar just starts fresh.
      } finally {
        setResuming(false);
      }
    })();
  }, [open, loadChat, onEmpty, onResume, hasStarted]);

  return resuming;
}
